import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Login from './components/Login';
import Navbar from './components/Navbar';
import DoctorDashboard from './components/DoctorDashboard';
import NursingDashboard from './components/NursingDashboard';
import DietCoordinatorDashboard from './components/DietCoordinatorDashboard';
import TreatmentCoordinatorDashboard from './components/TreatmentCoordinatorDashboard';
import PatientList from './components/PatientList';
import DietPlanManagement from './components/doctor/DietPlanManagement';
import TreatmentPlanManagement from './components/doctor/TreatmentPlanManagement';
import ViewIPDPlans from './components/reception/ViewIPDPlans';
import DischargePrescription from './components/doctor/DischargePrescription';
import IPDPatientManager from './components/doctor/IPDPatientManager';
import DayCarePatientManager from './components/doctor/DayCarePatientManager';
import DayCareRegistration from './components/DayCareRegistration';
import BillingModule from './components/billing/BillingModule';
import ServicePackages from './components/ServicePackages';
import AdminDashboard from './components/admin/AdminDashboard';
import VitalsRecording from './components/nursing/VitalsRecording';
import VitalsHistory from './components/nursing/VitalsHistory';
import PublicPatientRegistration from './components/PublicPatientRegistration';
import { useLocation, useNavigate } from 'react-router-dom';

function DayCareRegistrationPage({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div>
      <Navbar user={user} onLogout={onLogout} />
      <Box sx={{ p: 3 }}>
        <DayCareRegistration
          preselectedPatient={location.state?.preselectedPatient || null}
          onSuccess={() => navigate('/reception/patients')}
          onCancel={() => navigate(-1)}
        />
      </Box>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }

    const onAuthLogout = () => setUser(null);
    window.addEventListener('auth:logout', onAuthLogout);
    return () => window.removeEventListener('auth:logout', onAuthLogout);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <Router>
      <Routes>
        {/* Public routes - accessible without login */}
        <Route path="/register" element={<PublicPatientRegistration />} />

        {/* Protected routes */}
        {!user ? (
          <Route path="*" element={<Login onLogin={handleLogin} />} />
        ) : (
          <>
            {user.role === 'doctor' && (
              <>
                <Route path="/doctor" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <DoctorDashboard />
                  </div>
                } />
                <Route path="/doctor/patients" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <PatientList />
                  </div>
                } />
                <Route path="/doctor/ipd-diet-plan/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <DietPlanManagement />
                  </div>
                } />
                <Route path="/doctor/ipd-treatment-plan/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <TreatmentPlanManagement />
                  </div>
                } />
                <Route path="/doctor/ipd-patient/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <IPDPatientManager />
                  </div>
                } />
                <Route path="/doctor/day-care/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <DayCarePatientManager />
                  </div>
                } />
                <Route path="/doctor/discharge-prescription/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <DischargePrescription />
                  </div>
                } />
                <Route path="/doctor/vitals-history/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <VitalsHistory />
                  </div>
                } />
                <Route path="*" element={<Navigate to="/doctor" replace />} />
              </>
            )}
            {user.role === 'reception' && (
              <>
                <Route path="/reception" element={<Navigate to="/reception/patients" replace />} />
                <Route path="/reception/patients" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <PatientList />
                  </div>
                } />
                <Route path="/reception/ipd-plans/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <ViewIPDPlans />
                  </div>
                } />
                <Route path="/reception/day-care" element={
                  <DayCareRegistrationPage user={user} onLogout={handleLogout} />
                } />
                <Route path="/reception/billing" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <BillingModule />
                  </div>
                } />
                <Route path="/reception/service-packages" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <ServicePackages />
                  </div>
                } />
                <Route path="*" element={<Navigate to="/reception/patients" replace />} />
              </>
            )}
            {user.role === 'nursing' && (
              <>
                <Route path="/nursing" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <NursingDashboard />
                  </div>
                } />
                <Route path="/nursing/vitals/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <VitalsRecording />
                  </div>
                } />
                <Route path="/nursing/vitals/history/:admissionId" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <VitalsHistory />
                  </div>
                } />
                <Route path="*" element={<Navigate to="/nursing" replace />} />
              </>
            )}
            {user.role === 'diet_coordinator' && (
              <>
                <Route path="/diet-coordinator" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <DietCoordinatorDashboard />
                  </div>
                } />
                <Route path="*" element={<Navigate to="/diet-coordinator" replace />} />
              </>
            )}
            {user.role === 'treatment_coordinator' && (
              <>
                <Route path="/treatment-coordinator" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <TreatmentCoordinatorDashboard />
                  </div>
                } />
                <Route path="*" element={<Navigate to="/treatment-coordinator" replace />} />
              </>
            )}
            {user.role === 'finance' && (
              <>
                <Route path="/finance/billing" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <BillingModule />
                  </div>
                } />
                <Route path="*" element={<Navigate to="/finance/billing" replace />} />
              </>
            )}
            {user.role === 'admin' && (
              <>
                <Route path="/admin" element={
                  <div>
                    <Navbar user={user} onLogout={handleLogout} />
                    <AdminDashboard user={user} />
                  </div>
                } />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </>
            )}
          </>
        )}
      </Routes>
    </Router>
  );
}

export default App;
